import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '@/hooks/useCurrency';
import { FRAME_LIKE_TYPES, LensInclusion, ProductType, type ProductDetail } from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Drawer } from '@/components/ui/Drawer';
import { FormField } from '@/components/ui/FormField';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { apiErrorMessage } from '@/services/apiClient';
import { useCategoriesQuery } from '@/modules/inventory/api/inventory.api';
import {
  useBrandsQuery,
  useCreateProductMutation,
  useUpdateProductMutation,
} from '../api/catalog.api';
import { categoryLabel } from '../category-label';
import {
  buildVariantPayload,
  emptyVariantFormFor,
  EMPTY_VARIANT_FORM,
  hasVariantErrors,
  validateVariantForm,
  type VariantFormErrors,
  type VariantFormValues,
} from '../variant-form';
import { VariantDetailFields } from './VariantDetailFields';

interface ProductFormDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  /** Absent when creating. */
  product?: ProductDetail;
  onSaved: (product: ProductDetail, message: string) => void;
}

interface ProductFormValues {
  type: ProductType;
  name: string;
  nameAr: string;
  description: string;
  brandId: string;
  categoryId: string;
  /** Frame-like types only; empty means not recorded. */
  lensInclusion: LensInclusion | '';
}

/** A sensible starting point for a new product, which the user can always change: a frame usually comes without lenses, sunglasses with them. */
function defaultLensInclusion(type: ProductType): LensInclusion | '' {
  if (type === ProductType.FRAME) return LensInclusion.WITHOUT_LENSES;
  if (type === ProductType.SUNGLASSES) return LensInclusion.WITH_LENSES;
  return '';
}

const EMPTY_PRODUCT_FORM: ProductFormValues = {
  type: ProductType.FRAME,
  name: '',
  nameAr: '',
  description: '',
  brandId: '',
  categoryId: '',
  lensInclusion: defaultLensInclusion(ProductType.FRAME),
};

/**
 * Create or edit a product.
 *
 * On creation the first variant is captured in the same drawer, so one pass produces something
 * sellable rather than a product nobody can put on a shelf. The type is fixed after creation
 * because it decides which typed detail table the variants live in.
 */
export function ProductFormDrawer({ isOpen, onClose, product, onSaved }: ProductFormDrawerProps) {
  const { t, i18n } = useTranslation();
  const { currencyCode } = useCurrency();
  const isEditing = Boolean(product);

  const [values, setValues] = useState<ProductFormValues>(EMPTY_PRODUCT_FORM);
  const [variant, setVariant] = useState<VariantFormValues>(EMPTY_VARIANT_FORM);
  const [nameError, setNameError] = useState<string | null>(null);
  const [lensInclusionError, setLensInclusionError] = useState<string | null>(null);
  const [variantErrors, setVariantErrors] = useState<VariantFormErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const brandsQuery = useBrandsQuery();
  const categoriesQuery = useCategoriesQuery();
  const createProduct = useCreateProductMutation();
  const updateProduct = useUpdateProductMutation(product?.id ?? '');
  const mutation = isEditing ? updateProduct : createProduct;

  useEffect(() => {
    if (!isOpen) return;

    setValues(
      product
        ? {
            type: product.type,
            name: product.name,
            nameAr: product.nameAr ?? '',
            description: product.description ?? '',
            brandId: product.brandId ?? '',
            categoryId: product.categoryId ?? '',
            lensInclusion: product.lensInclusion ?? '',
          }
        : EMPTY_PRODUCT_FORM,
    );
    setVariant(emptyVariantFormFor(EMPTY_PRODUCT_FORM.type));
    setNameError(null);
    setLensInclusionError(null);
    setVariantErrors({});
    setSubmitError(null);
  }, [isOpen, product]);

  const typeOptions = Object.values(ProductType).map((value) => ({
    value,
    label: t(`catalog.types.${value}`),
  }));

  const brandOptions = [
    { value: '', label: t('catalog.fields.noBrand') },
    ...(brandsQuery.data?.items ?? []).map((brand) => ({ value: brand.id, label: brand.name })),
  ];

  // Sub-categories are indented so the tenant's two-level structure is readable in a flat select.
  const categoryOptions = [
    { value: '', label: t('catalog.fields.noCategory') },
    ...(categoriesQuery.data?.items ?? []).map((category) => ({
      value: category.id,
      label: categoryLabel(category, i18n.language),
    })),
  ];

  const isFrameLike = FRAME_LIKE_TYPES.includes(values.type);
  // A legacy product may have no value on file; it can stay that way until someone records one.
  const lensInclusionOptions = [
    ...(isEditing && !product?.lensInclusion
      ? [{ value: '', label: t('catalog.fields.notSpecified') }]
      : []),
    ...Object.values(LensInclusion).map((value) => ({
      value,
      label: t(`catalog.lensInclusion.${value}`),
    })),
  ];

  // Switching type starts its fields from that type's defaults, so nothing typed for one type leaks into another.
  const changeType = (type: ProductType) => {
    setValues((c) => ({ ...c, type, lensInclusion: defaultLensInclusion(type) }));
    setVariant((c) => ({ ...c, details: emptyVariantFormFor(type).details }));
    setVariantErrors({});
    setLensInclusionError(null);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitError(null);

    if (values.name.trim().length < 2) {
      setNameError(t('catalog.validation.nameRequired'));
      return;
    }
    setNameError(null);

    // A new frame or pair of sunglasses must say whether it comes with lenses.
    if (!isEditing && isFrameLike && !values.lensInclusion) {
      setLensInclusionError(t('catalog.validation.required'));
      return;
    }
    setLensInclusionError(null);

    if (isEditing && product) {
      updateProduct.mutate(
        {
          name: values.name.trim(),
          nameAr: values.nameAr.trim(),
          description: values.description.trim(),
          brandId: values.brandId,
          categoryId: values.categoryId,
          ...(isFrameLike && values.lensInclusion ? { lensInclusion: values.lensInclusion } : {}),
        },
        {
          onSuccess: (saved) => {
            onSaved(saved, t('catalog.products.updated'));
            onClose();
          },
          onError: (caught) =>
            setSubmitError(apiErrorMessage(caught) ?? t('catalog.products.saveError')),
        },
      );
      return;
    }

    const validation = validateVariantForm(variant, values.type, t, currencyCode);
    setVariantErrors(validation);
    if (hasVariantErrors(validation)) return;

    createProduct.mutate(
      {
        type: values.type,
        name: values.name.trim(),
        ...(values.nameAr.trim() ? { nameAr: values.nameAr.trim() } : {}),
        ...(values.description.trim() ? { description: values.description.trim() } : {}),
        ...(values.brandId ? { brandId: values.brandId } : {}),
        ...(values.categoryId ? { categoryId: values.categoryId } : {}),
        ...(isFrameLike && values.lensInclusion ? { lensInclusion: values.lensInclusion } : {}),
        variants: [buildVariantPayload(variant, values.type, currencyCode)],
      },
      {
        onSuccess: (saved) => {
          onSaved(saved, t('catalog.products.created'));
          onClose();
        },
        onError: (caught) =>
          setSubmitError(apiErrorMessage(caught) ?? t('catalog.products.saveError')),
      },
    );
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? t('catalog.products.editTitle') : t('catalog.products.createTitle')}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" form="product-form" isLoading={mutation.isPending}>
            {isEditing ? t('common.save') : t('catalog.products.create')}
          </Button>
        </>
      }
    >
      <form id="product-form" onSubmit={handleSubmit} noValidate className="space-y-5">
        {submitError ? <Alert tone="error">{submitError}</Alert> : null}

        <FormField label={t('catalog.fields.type')} required>
          {({ id }) => (
            <Select
              id={id}
              options={typeOptions}
              value={values.type}
              // The type decides the variants' detail table, so it cannot move afterwards.
              disabled={isEditing}
              onChange={(event) => changeType(event.target.value as ProductType)}
            />
          )}
        </FormField>

        <Input
          label={t('catalog.fields.name')}
          required
          value={values.name}
          error={nameError ?? undefined}
          onChange={(event) => setValues((c) => ({ ...c, name: event.target.value }))}
        />

        <Input
          label={t('catalog.fields.nameAr')}
          dir="rtl"
          value={values.nameAr}
          onChange={(event) => setValues((c) => ({ ...c, nameAr: event.target.value }))}
        />

        <FormField label={t('catalog.fields.brand')}>
          {({ id }) => (
            <Select
              id={id}
              options={brandOptions}
              value={values.brandId}
              onChange={(event) => setValues((c) => ({ ...c, brandId: event.target.value }))}
            />
          )}
        </FormField>

        <FormField label={t('catalog.fields.category')} hint={t('catalog.fields.categoryHint')}>
          {({ id, describedBy }) => (
            <Select
              id={id}
              aria-describedby={describedBy}
              options={categoryOptions}
              value={values.categoryId}
              onChange={(event) => setValues((c) => ({ ...c, categoryId: event.target.value }))}
            />
          )}
        </FormField>

        {isFrameLike ? (
          <FormField
            label={t('catalog.fields.lensInclusion')}
            required={!isEditing}
            error={lensInclusionError ?? undefined}
          >
            {({ id, describedBy }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                options={lensInclusionOptions}
                value={values.lensInclusion}
                hasError={Boolean(lensInclusionError)}
                onChange={(event) =>
                  setValues((c) => ({
                    ...c,
                    lensInclusion: event.target.value as LensInclusion | '',
                  }))
                }
              />
            )}
          </FormField>
        ) : null}

        <FormField label={t('catalog.fields.description')}>
          {({ id }) => (
            <textarea
              id={id}
              rows={2}
              maxLength={2000}
              className="border-line focus:border-brand-500 focus:ring-brand-100 w-full rounded-lg border bg-white px-3 py-2.5 text-sm focus:ring-4 focus:outline-none"
              value={values.description}
              onChange={(event) => setValues((c) => ({ ...c, description: event.target.value }))}
            />
          )}
        </FormField>

        {!isEditing ? (
          <div className="border-line space-y-4 border-t pt-4">
            <div>
              <h3 className="text-ink text-sm font-semibold">
                {t('catalog.products.firstVariant')}
              </h3>
              <p className="text-muted mt-0.5 text-xs">{t('catalog.products.firstVariantHint')}</p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Input
                label={t('catalog.fields.sellingPrice')}
                required
                type="number"
                step="0.01"
                min={0}
                dir="ltr"
                value={variant.sellingPrice}
                error={variantErrors.sellingPrice}
                onChange={(event) =>
                  setVariant((c) => ({ ...c, sellingPrice: event.target.value }))
                }
              />
              <Input
                label={t('catalog.fields.barcode')}
                dir="ltr"
                hint={t('catalog.fields.barcodeHint')}
                value={variant.barcode}
                error={variantErrors.barcode}
                onChange={(event) => setVariant((c) => ({ ...c, barcode: event.target.value }))}
              />
            </div>

            <VariantDetailFields
              type={values.type}
              values={variant.details}
              errors={variantErrors.details ?? {}}
              onChange={(key, value) =>
                setVariant((c) => ({ ...c, details: { ...c.details, [key]: value } }))
              }
            />
          </div>
        ) : null}
      </form>
    </Drawer>
  );
}
